// LogRocket URL sanitizer (LP-2622): strips tokens and PII from page URLs, referrers and captured request URLs.
// Only LogRocket's copy of the URL changes; the browser location and routing are untouched.
//
// Default-deny, like the rest of deposit's sanitizing: a path segment is kept only when it looks like a route or file
// name, and a query value only when its name is on SAFE_QUERY_PARAMS and the value looks harmless.

import {SAFE_QUERY_PARAMS} from './logrocket-pii-keys';
import {isSafeValue, REDACTED} from './logrocket-redaction.util';

/** Route names and file names: letters, dots, underscores and hyphens only. */
const SAFE_PATH_SEGMENT = /^[A-Za-z][A-Za-z._-]{0,31}$/;
/** API prefixes that legitimately contain digits. */
const SAFE_PATH_SEGMENT_WITH_DIGITS = /^(csb2b|v\d{1,2})$/i;
/** Page routes whose next segment is a token: s/:code (member key), r/:code and cr/:code (re-entry). */
const TOKEN_ROUTES = new Set(['s', 'r', 'cr']);

function sanitizePath(pathname: string): string {
    const segments = pathname.split('/');
    return segments.map((segment, index) => {
        if (!segment) {
            return segment;
        }
        // segments[0] is always '' for an absolute path, so the route name is segments[1].
        if (index === 2 && TOKEN_ROUTES.has(segments[1].toLowerCase())) {
            return REDACTED;
        }
        return SAFE_PATH_SEGMENT.test(segment) || SAFE_PATH_SEGMENT_WITH_DIGITS.test(segment) ? segment : REDACTED;
    }).join('/');
}

/** Strips tokens, ids and sensitive query values from a URL. Returns null when the URL cannot be parsed. */
export function sanitizeLogRocketUrl(url: string | null | undefined): string | null {
    if (!url) {
        return null;
    }
    try {
        const parsed = new URL(url, window.location.origin);
        parsed.pathname = sanitizePath(parsed.pathname);
        const params = new URLSearchParams();
        parsed.searchParams.forEach((value, key) => {
            params.append(key, SAFE_QUERY_PARAMS.has(key.toLowerCase()) && isSafeValue(value) ? value : REDACTED);
        });
        parsed.search = params.toString();
        // Fragments can carry tokens (e.g. cross-sell #pwd=, OAuth responses); replay never needs them.
        parsed.hash = '';
        parsed.username = '';
        parsed.password = '';
        return parsed.toString();
    } catch {
        return null;
    }
}
